import React, { createContext, useContext, useMemo } from 'react';

import invariant from 'tiny-invariant';

import type { UserMenuDataShapes } from '../../../common/types';
import { buildUserMenuDataShapes } from '../../../common/utils/build-user-menu-data-shapes';
import { useItemsAndShortcuts } from '../../items-and-shortcuts-provider';
import { useOptions } from '../../options-provider';
import type { UserMenuDataShapesProviderProps } from './types';

export const UserMenuDataShapesContext: React.Context<UserMenuDataShapes | undefined> =
	createContext<UserMenuDataShapes | undefined>(undefined);

/**
 * Stores the repaired user menu data, in a variety of shapes, and makes it available to child components.
 * This component exists so that it is easy for child components to read and update the user menu data independently of the user side nav data.
 */
export function UserMenuDataShapesProvider({
	children,
	rawUserMenuData,
	setUserMenuData,
}: UserMenuDataShapesProviderProps): React.JSX.Element {
	const { disableMenuCustomization, defaultMenuVisibility } = useOptions();
	const { allItems, forYouMenuId, globalItems, shortcuts } = useItemsAndShortcuts();

	const userMenuDataShapes = useMemo(
		(): UserMenuDataShapes =>
			buildUserMenuDataShapes({
				allItemMenuIds: Object.keys(allItems),
				allShortcutMenuIds: Object.keys(shortcuts ?? {}),
				forYouMenuId,
				globalItems,
				// All menu items are visible when there is no user menu data
				rawUserMenuData: disableMenuCustomization ? [] : rawUserMenuData,
				setUserMenuData,
				defaultMenuVisibility,
			}),
		[
			allItems,
			disableMenuCustomization,
			forYouMenuId,
			globalItems,
			rawUserMenuData,
			setUserMenuData,
			shortcuts,
			defaultMenuVisibility,
		],
	);

	return (
		<UserMenuDataShapesContext.Provider value={userMenuDataShapes}>
			{children}
		</UserMenuDataShapesContext.Provider>
	);
}

export function useUserMenuDataShapes(): UserMenuDataShapes {
	const context = useContext(UserMenuDataShapesContext);

	invariant(
		context,
		'useUserMenuDataShapes must be used inside an initialized UserMenuDataShapesProvider',
	);

	return context;
}
